import { PORTAL_TOKEN_HEADER, buildPath, routes } from '@garage/shared';
import { useEffect, useRef, useState } from 'react';
import { fileUrl } from '../shared/api.js';
import { usePortalPreview } from './preview.js';
import { getPortalToken } from './token.js';

/**
 * Image du portail, chargée avec le jeton en en-tête (une balise <img> ne peut pas l'envoyer) puis
 * affichée par une URL d'objet locale. Chargement différé : l'image n'est téléchargée que
 * lorsqu'elle approche de l'écran (économie de données en 4G).
 */
export function usePortalImageUrl(url: string | null) {
  // Aperçu côté garage (vague 7) : session du membre, sans jeton.
  const preview = usePortalPreview() !== null;
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (visible || !ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !url) return;
    const token = preview ? null : getPortalToken();
    if (!preview && !token) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    const init: RequestInit = token ? { headers: { [PORTAL_TOKEN_HEADER]: token }, credentials: 'omit' } : { credentials: 'same-origin' };
    fetch(url, init)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, visible, preview]);

  return { ref, src, failed };
}

/** Photo publiée du dossier (`portal.mediaFile`). */
export function usePortalImage(id: string, variant: 'thumb' | 'display') {
  const preview = usePortalPreview();
  return usePortalImageUrl(preview ? preview.mediaUrl(id, variant) : fileUrl('portal.mediaFile', id, variant));
}

/** Photo du véhicule (`portal.vehiclePhoto`), seulement si le garage l'a activée (vague 4). */
export function vehiclePhotoUrl(variant: 'thumb' | 'display'): string {
  return `${buildPath(routes['portal.vehiclePhoto'].path, {})}?variant=${variant}`;
}
